import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableContainer, TableHead, Paper, TableRow, TableCell } from '@mui/material';
import ArrayChipLogic from '@/components/UI/ArrayChips/ArrayChipLogic';
import { Language, LimitationVolume } from '@/types';
import styles from './LimitationTable.module.scss';

interface LimitationTableProps {
  volume: LimitationVolume;
  headerLanguage: Language;
}

interface RowHeaderProps {
  titles: string[];
}

interface RowProps {
  category: string;
  selected?: ReactNode;
  field?: string;
  elements?: ReactNode;
  comment?: string;
  elementsWithContent?: boolean;
}

const LimitationTable: React.FC<LimitationTableProps> = (props) => {
  const { volume, headerLanguage } = props;
  const { t } = useTranslation();

  const getAffectedText = (field: string) => <span className={styles.AffectedIcon}>{t(`affectedVehicles.${field}`)}</span>;

  const RowHeader: React.FC<RowHeaderProps> = ({ titles }) => (
    <TableRow>{titles.map((title) => <TableCell key={title}>{title}</TableCell>)}</TableRow>);

  const Row: React.FC<RowProps> = ({ category, selected, field, elements, comment, elementsWithContent }) => {
    const hasElements = Boolean(elementsWithContent || (field && volume[`${field}Selecteds`]?.length !== 0));
    const selectedContent = selected || getAffectedText(volume[`${field}SelectionAccording`]);

    return (
      <TableRow>
        <TableCell>{t(category)}</TableCell>
        <TableCell>{hasElements && selectedContent}</TableCell>
        <TableCell>{elements ?? <ArrayChipLogic id={`chips-${category}`} detailTitle={t(category)} chipData={volume[`${field}Selecteds`]} />}</TableCell>
        <TableCell>{comment || volume[`${field}Comment`]?.[headerLanguage] || '-'}</TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer component={Paper} className={styles.Table}>
      <Table aria-label="Limitation">
        <TableHead>
          <RowHeader titles={[t('category'), t('selected'), t('elements'), t('comment')]} />
        </TableHead>
        <TableBody>
          <Row
            category="affectedVehicles.vehicleModelDesignation.title"
            field="vehicleModelDesignation"
            elements={(
              <ArrayChipLogic
                id="vehicle_model_designation_chips"
                detailTitle={t('affectedVehicles.vehicleModelDesignation.title')}
                chipData={volume.vehicleModelDesignationSelecteds?.map((m) => JSON.parse(m)?.modelDesignation)}
              />
            )}
          />
          <Row
            category="affectedVehicles.AGGREGATE"
            field="aggregate"
            elements={(
              <ArrayChipLogic
                id="aggregate_chips"
                detailTitle={t('affectedVehicles.AGGREGATE')}
                chipData={volume.aggregateSelecteds.map((e) => JSON.parse(e)?.aggregateCode)}
              />
            )}
          />
          <Row category="affectedVehicles.COUNTRY_VARIANTS" field="countryVariants" />
          <Row category="affectedVehicles.PAINT_CODE" field="paintCode" />
          {volume.codeRules.map((rule) => (
            <Row
              key={rule.codeRulesDescription}
              category="affectedVehicles.CODE_RULE"
              selected={getAffectedText(rule.codeRulesSelectionAccording)}
              elements={rule.codeRulesDescription}
              elementsWithContent={rule.codeRulesDescription !== ''}
              comment={rule.codeRuleComment.translations[headerLanguage]}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LimitationTable;
